import type { Message } from '@/types/messages.ts';
import type { ReviewFinding } from '@/lib/conversationReviews.ts';

/* Long enough to tell two turns apart, short enough to survive the reviewer trimming a quote. */
const QUOTE_PREFIX_LENGTH = 60;

/**
 * The message a finding's evidence was quoted from: the turn in the finding's
 * minute that holds the quote, else any turn holding it, else the first visible
 * turn of that minute. Tool rows and pauses never count as the quoted line.
 */
export function findQuotedMessage(
  messages: Message[],
  finding: ReviewFinding,
  timezone: string,
): Message | null {
  const quote = normalise(finding.evidence).slice(0, QUOTE_PREFIX_LENGTH);
  const holdsQuote = (message: Message) =>
    quote !== '' && normalise(wordsOf(message.body)).includes(quote);

  const atQuotedTime = finding.quoted_at
    ? messages.filter(
        (message) => transcriptTime(message, timezone) === finding.quoted_at,
      )
    : [];
  return (
    atQuotedTime.find(holdsQuote) ??
    messages.find(holdsQuote) ??
    atQuotedTime.find(isVisibleTurn) ??
    null
  );
}

function isVisibleTurn(message: Message): boolean {
  return (
    (message.type === 'incoming_message' || message.type === 'outgoing_message') &&
    message.body.type !== 'pause'
  );
}

/* A notification is stamped with when it went out, as the transcript stamps it. */
function transcriptTime(message: Message, timezone: string): string {
  const at =
    message.body.type === 'notification'
      ? (message.sent_at ?? message.created_at)
      : message.created_at;
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    })
      .formatToParts(new Date(at))
      .map((part) => [part.type, part.value]),
  );
  return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}`;
}

function wordsOf(value: unknown): string {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map(wordsOf).join(' ');
  if (value && typeof value === 'object') {
    return Object.values(value).map(wordsOf).join(' ');
  }
  return '';
}

/* Markdown marks and line breaks differ between the transcript and the stored body. */
function normalise(text: string): string {
  return text
    .replace(/[*_`#>]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}
